import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Wallet } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString("en-AU", { maximumFractionDigits: 2 })}`;

function CostSplitDialog({ uid, name, open, onClose, onSaved }) {
  const [d, setD] = useState(null);
  const [cost, setCost] = useState("");
  const [split, setSplit] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get(`/agency/users/${uid}/monthly-cost`).then(({ data }) => { setD(data); setCost(data.monthly_cost ? String(data.monthly_cost) : ""); setSplit(Object.fromEntries(Object.entries(data.split || {}).map(([k, v]) => [k, String(v)]))); });
  }, [open, uid]);
  const total = Object.values(split).reduce((a, v) => a + (Number(v) || 0), 0);
  const num = Number(cost) || 0;
  const ok = num === 0 || Math.abs(total - 100) < 0.01;
  const rows = d ? [...d.clients, { id: "agency", name: "Agency (internal)" }] : [];
  const save = async () => {
    setBusy(true);
    try { await api.put(`/agency/users/${uid}/monthly-cost`, { monthly_cost: num, split: Object.fromEntries(Object.entries(split).map(([k, v]) => [k, Number(v) || 0])) }); toast.success("Monthly cost saved"); onSaved(num); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-[#EEEFEF] sm:max-w-lg" data-testid="cost-split-dialog">
        <DialogHeader><DialogTitle className="font-display">Monthly cost · {name}</DialogTitle><DialogDescription>What this person costs the business each month, split across their clients and Agency.</DialogDescription></DialogHeader>
        {!d ? <Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc] mx-auto my-6" /> : <div className="space-y-4">
          <div><label className="text-xs text-[#9FA1A4] mb-1 block">Total monthly cost (AUD)</label>
            <Input data-testid="cost-split-total" type="number" min="0" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="e.g. 8500" className="bg-[#F6F6FD] border-[#EEEFEF]" /></div>
          {d.clients.length === 0 && <p className="text-[11px] text-amber-700">This person isn't assigned to any clients, so their cost can only go to Agency.</p>}
          <div className="space-y-1.5">
            {rows.map((r) => (
              <div key={r.id} className="flex items-center gap-3" data-testid={`cost-split-row-${r.id}`}>
                <span className="text-sm text-[#06060C] flex-1 truncate">{r.name}</span>
                <div className="relative w-24"><Input data-testid={`cost-split-pct-${r.id}`} type="number" min="0" max="100" value={split[r.id] ?? ""} onChange={(e) => setSplit({ ...split, [r.id]: e.target.value })} placeholder="0" className="pr-6 bg-[#F6F6FD] border-[#EEEFEF] h-8 text-right" /><span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-[#9FA1A4]">%</span></div>
                <span className="w-24 text-right font-mono-data text-xs text-[#595D62]">{money(num * (Number(split[r.id]) || 0) / 100)}</span>
              </div>
            ))}
          </div>
          <div className={`flex items-center justify-between rounded-xl px-3 py-2 text-xs ${ok ? "bg-[#E6F7EE] text-[#1f9d55]" : "bg-rose-50 text-rose-700"}`} data-testid="cost-split-sum">
            <span>Total split</span><span className="font-mono-data font-semibold">{Math.round(total * 100) / 100}% {ok ? "" : "· must be 100%"}</span>
          </div>
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose} className="rounded-full">Cancel</Button>
            <Button data-testid="cost-split-save" onClick={save} disabled={busy || !ok} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button></div>
        </div>}
      </DialogContent>
    </Dialog>
  );
}

export const MonthlyCostField = ({ user }) => {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(user.monthly_cost || 0);
  return (
    <div>
      <label className="text-xs text-[#9FA1A4] mb-1 block">Monthly cost to business (AUD)</label>
      <button type="button" data-testid="edit-monthly-cost" onClick={() => setOpen(true)} className="w-full flex items-center justify-between rounded-md border border-[#EEEFEF] bg-[#F6F6FD] px-3 h-9 text-sm hover:border-[#C9B8FF] transition-colors">
        <span className="flex items-center gap-2 text-[#06060C]"><Wallet className="h-3.5 w-3.5 text-[#723FFF]" />{val ? money(val) : "Not set"}</span>
        <span className="text-xs text-[#5b2fd6]">Set split</span>
      </button>
      <CostSplitDialog uid={user.id} name={user.name} open={open} onClose={() => setOpen(false)} onSaved={setVal} />
    </div>
  );
};
